import React from 'react';
import { Button, Input, Select, Space, Typography, Tag, Tooltip, InputNumber, Dropdown } from 'antd';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, LinkOutlined, ThunderboltOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface InlineButton {
  text: string;
  type: 'url' | 'url_confirm' | 'callback_switch' | 'navigate' | 'web_app';
  url?: string;
  value?: string;
  alt_text?: string;
  alt_buttons?: InlineButtonLayout;
  confirm_text?: string;
  targetPageIndex?: number;
}

export type InlineButtonRow = InlineButton[];
export type InlineButtonLayout = InlineButtonRow[];

interface PageOption {
  pageIndex: number;
  title: string;
}

interface Props {
  value?: InlineButtonLayout;
  onChange?: (v: InlineButtonLayout) => void;
  /** 动态变量字段名列表，用于显示可用变量提示 */
  dynamicVarFields?: string[];
  /** 登录短码变量名列表 */
  authCodeVarNames?: string[];
  /** 可用的导航目标页面（用于 navigate 类型按钮） */
  availablePages?: PageOption[];
  /** 当前编辑的页面索引（navigate 时排除自身） */
  currentPageIndex?: number;
}

const BUTTON_TYPES = [
  { value: 'url', label: '直接跳转 (url)' },
  { value: 'url_confirm', label: '确认后跳转 (url_confirm)' },
  { value: 'callback_switch', label: '切换内容 (callback_switch)' },
  { value: 'navigate', label: '跳转到页面 (navigate)' },
  { value: 'web_app', label: '打开 Mini App (web_app)' },
];

const STATIC_VARS = [
  '{user_id}', '{username}', '{first_name}', '{last_name}',
  '{bot_id}', '{bot_name}', '{bot_invite_link}',
  '{start}',
];

const URL_PRESETS = [
  {
    key: 'bot_invite_admin',
    label: '拉入群组 + 设为管理员',
    url: '{bot_invite_link}',
    description: '用户点击后选择群组，自动添加机器人并授予管理员权限',
  },
  {
    key: 'bot_invite_admin_ban',
    label: '拉入群组 + 管理员(含封禁)',
    url: '{bot_invite_link}&admin=ban_users',
    description: '同上，额外包含封禁用户权限',
  },
];

const emptyButton = (): InlineButton => ({ text: '', type: 'url', url: '' });

const InlineButtonEditor: React.FC<Props> = ({
  value = [],
  onChange,
  dynamicVarFields = [],
  authCodeVarNames = [],
  availablePages = [],
  currentPageIndex,
}) => {
  const rows: InlineButtonLayout = value || [];

  const update = (next: InlineButtonLayout) => onChange?.(next);

  const addRow = () => update([...rows, [emptyButton()]]);

  const removeRow = (rowIdx: number) =>
    update(rows.filter((_, i) => i !== rowIdx));

  const addButton = (rowIdx: number) =>
    update(rows.map((row, i) => i === rowIdx ? [...row, emptyButton()] : row));

  const removeButton = (rowIdx: number, btnIdx: number) =>
    update(
      rows
        .map((row, i) => i === rowIdx ? row.filter((_, j) => j !== btnIdx) : row)
        .filter(row => row.length > 0)
    );

  const updateButton = (rowIdx: number, btnIdx: number, patch: Partial<InlineButton>) =>
    update(
      rows.map((row, i) =>
        i === rowIdx
          ? row.map((btn, j) => j === btnIdx ? { ...btn, ...patch } : btn)
          : row
      )
    );

  const allVarHints = [
    ...STATIC_VARS,
    ...dynamicVarFields.map(f => `{${f}}`),
    ...authCodeVarNames.map(n => `{${n}}`),
  ];

  const varHintTip = allVarHints.length > 0
    ? `可用变量：${allVarHints.join('  ')}`
    : '可用变量：{user_id}  {username}  {first_name}  {bot_invite_link}';

  // Navigate 可用目标页面（排除当前页）
  const navTargets = availablePages.filter(
    p => p.pageIndex !== currentPageIndex
  );

  const renderButtonFields = (btn: InlineButton, rowIdx: number, btnIdx: number) => {
    if (btn.type === 'url' || btn.type === 'url_confirm') {
      const presetMatch = URL_PRESETS.find(p => p.url === btn.url);
      return (
        <>
          <div style={{ paddingLeft: 48, marginTop: 4 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Input
                size="small"
                style={{ flex: 1 }}
                placeholder="URL，支持变量，如 https://app.com/login?token={login_token}"
                value={btn.url || ''}
                onChange={e => updateButton(rowIdx, btnIdx, { url: e.target.value })}
                suffix={
                  <Tooltip title={varHintTip} placement="topRight">
                    <InfoCircleOutlined style={{ color: '#1677ff', cursor: 'help' }} />
                  </Tooltip>
                }
              />
              <Dropdown
                menu={{
                  items: URL_PRESETS.map(preset => ({
                    key: preset.key,
                    label: (
                      <div>
                        <div>{preset.label}</div>
                        <div style={{ fontSize: 11, color: '#999' }}>{preset.description}</div>
                      </div>
                    ),
                    onClick: () => updateButton(rowIdx, btnIdx, { url: preset.url }),
                  })),
                }}
                placement="bottomRight"
                trigger={['click']}
              >
                <Tooltip title="快捷填充预设链接">
                  <Button size="small" icon={<ThunderboltOutlined />} style={{ color: '#faad14' }} />
                </Tooltip>
              </Dropdown>
            </div>
            {presetMatch && (
              <Text type="success" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                已选预设：{presetMatch.label}
              </Text>
            )}
          </div>
          {btn.type === 'url_confirm' && (
            <div style={{ paddingLeft: 48, marginTop: 4 }}>
              <Input
                size="small"
                placeholder="确认提示文字（可选，留空使用默认）"
                value={btn.confirm_text || ''}
                onChange={e => updateButton(rowIdx, btnIdx, { confirm_text: e.target.value })}
              />
            </div>
          )}
        </>
      );
    }

    if (btn.type === 'callback_switch') {
      return (
        <div style={{ paddingLeft: 48, marginTop: 4 }}>
          <Input
            size="small"
            placeholder="点击后显示的消息内容（替代文本），支持变量"
            value={btn.alt_text || ''}
            onChange={e => updateButton(rowIdx, btnIdx, { alt_text: e.target.value })}
            suffix={
              <Tooltip title={varHintTip} placement="topRight">
                <InfoCircleOutlined style={{ color: '#1677ff', cursor: 'help' }} />
              </Tooltip>
            }
          />
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
              替代按钮（可选）：点击后同时替换下方按钮，实现翻译/切换效果
            </Text>
            <InlineButtonEditor
              value={btn.alt_buttons || []}
              onChange={v => updateButton(rowIdx, btnIdx, { alt_buttons: v })}
              dynamicVarFields={dynamicVarFields}
              availablePages={availablePages}
            />
          </div>
        </div>
      );
    }

    if (btn.type === 'navigate') {
      if (navTargets.length === 0) {
        return (
          <div style={{ paddingLeft: 48, marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              请先在下方"子页面"区域添加子页面，再配置导航目标
            </Text>
          </div>
        );
      }
      return (
        <div style={{ paddingLeft: 48, marginTop: 4 }}>
          <Select
            size="small"
            style={{ width: 240 }}
            placeholder="选择目标页面"
            value={btn.targetPageIndex}
            onChange={val => updateButton(rowIdx, btnIdx, { targetPageIndex: val })}
            options={navTargets.map(p => ({
              value: p.pageIndex,
              label: p.pageIndex === 0
                ? '主页面'
                : `${p.title || `子页面 ${p.pageIndex}`}`,
            }))}
          />
        </div>
      );
    }

    if (btn.type === 'web_app') {
      return (
        <div style={{ paddingLeft: 48, marginTop: 4 }}>
          <Input
            size="small"
            placeholder="必须为 HTTPS，建议在 URL 中携带 botId，如 https://your-app.com/?botId=1"
            value={btn.url || btn.value || ''}
            onChange={e => updateButton(rowIdx, btnIdx, { url: e.target.value, value: undefined })}
            prefix={<LinkOutlined style={{ color: '#52c41a' }} />}
            status={
              (btn.url || btn.value || '').startsWith('http://')
                ? 'error'
                : undefined
            }
          />
          {(btn.url || btn.value || '').startsWith('http://') && (
            <Text type="danger" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
              web_app 按钮要求 HTTPS，HTTP 链接将被忽略
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            用户点击后在 Telegram 内直接打开你的 Web App（无需跳出 Telegram）
          </Text>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, padding: 12 }}>
      {rows.length === 0 && (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          暂无按钮，点击"添加行"开始配置。同一行中的按钮在 Telegram 中并排显示。
        </Text>
      )}

      {rows.map((row, rowIdx) => (
        <div
          key={rowIdx}
          style={{ marginBottom: 16, padding: 10, background: '#fafafa', borderRadius: 4, border: '1px solid #f0f0f0' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
            <Text strong style={{ marginRight: 4 }}>第 {rowIdx + 1} 行</Text>
            <Space size={4} style={{ flex: 1, flexWrap: 'wrap' }}>
              {row.map((btn, i) => (
                <Tag
                  key={i}
                  color={
                    btn.type === 'navigate' ? 'purple'
                    : btn.type === 'web_app' ? 'green'
                    : 'blue'
                  }
                  style={{ cursor: 'default' }}
                >
                  {(btn.type === 'navigate' || btn.type === 'web_app') && (
                    <LinkOutlined style={{ marginRight: 3 }} />
                  )}
                  {btn.text || `按钮${i + 1}`}
                </Tag>
              ))}
            </Space>
            <Button size="small" icon={<PlusOutlined />} onClick={() => addButton(rowIdx)}>
              添加按钮
            </Button>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeRow(rowIdx)}>
              删除行
            </Button>
          </div>

          {row.map((btn, btnIdx) => (
            <div
              key={btnIdx}
              style={{ marginBottom: 8, padding: '8px 10px', background: '#fff', borderRadius: 4, border: '1px solid #e8e8e8' }}
            >
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 12, minWidth: 40 }}>按钮 {btnIdx + 1}</Text>
                <Input
                  size="small"
                  placeholder="按钮文字"
                  style={{ width: 130 }}
                  value={btn.text}
                  onChange={e => updateButton(rowIdx, btnIdx, { text: e.target.value })}
                />
                <Select
                  size="small"
                  style={{ width: 200 }}
                  value={btn.type}
                  options={BUTTON_TYPES}
                  onChange={val => updateButton(rowIdx, btnIdx, { type: val, url: '', alt_text: '', targetPageIndex: undefined })}
                />
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeButton(rowIdx, btnIdx)} />
              </div>
              {renderButtonFields(btn, rowIdx, btnIdx)}
            </div>
          ))}
        </div>
      ))}

      <Button size="small" icon={<PlusOutlined />} onClick={addRow}>
        添加行
      </Button>
      <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
        同一行内的按钮在 Telegram 中从左到右并排显示
      </Text>
    </div>
  );
};

/**
 * 校验 inline buttons 配置完整性，返回错误信息或 null
 */
export function validateInlineButtons(buttons: InlineButtonLayout | undefined): string | null {
  if (!buttons || buttons.length === 0) return null;

  for (let rowIdx = 0; rowIdx < buttons.length; rowIdx++) {
    const row = buttons[rowIdx];
    for (let btnIdx = 0; btnIdx < row.length; btnIdx++) {
      const btn = row[btnIdx];
      const pos = `第 ${rowIdx + 1} 行按钮 ${btnIdx + 1}`;

      if (!btn.text || !btn.text.trim()) {
        return `${pos}：按钮文字不能为空`;
      }

      if (btn.type === 'url' || btn.type === 'url_confirm') {
        if (!btn.url && !btn.value) {
          return `${pos}：${btn.type === 'url' ? '跳转链接' : '确认跳转链接'}不能为空`;
        }
      }

      if (btn.type === 'callback_switch') {
        if (!btn.alt_text || !btn.alt_text.trim()) {
          return `${pos}：切换内容的替代文本不能为空`;
        }
      }

      if (btn.type === 'navigate') {
        if (btn.targetPageIndex === undefined || btn.targetPageIndex === null) {
          return `${pos}：请选择导航目标页面`;
        }
      }

      if (btn.type === 'web_app') {
        const webUrl = btn.url || btn.value || '';
        if (!webUrl) {
          return `${pos}：Mini App URL 不能为空`;
        }
        if (!webUrl.startsWith('https://') && !webUrl.includes('{')) {
          return `${pos}：Mini App URL 必须为 HTTPS`;
        }
      }
    }
  }
  return null;
}

/**
 * antd Form.Item rules 用的验证规则
 */
export const inlineButtonsRule = {
  validator(_: any, value: InlineButtonLayout) {
    const err = validateInlineButtons(value);
    return err ? Promise.reject(new Error(err)) : Promise.resolve();
  },
};

/**
 * 校验子页面中的按钮配置
 */
export const subPagesButtonsRule = {
  validator(_: any, subPages: any[]) {
    if (!subPages || subPages.length === 0) return Promise.resolve();
    for (const page of subPages) {
      if (page.buttons && page.buttons.length > 0) {
        const err = validateInlineButtons(page.buttons);
        if (err) {
          const title = page.title || `子页面 ${page.pageIndex}`;
          return Promise.reject(new Error(`${title} - ${err}`));
        }
      }
    }
    return Promise.resolve();
  },
};

export default InlineButtonEditor;
